import { InputSwitch } from "primereact/inputswitch";
import { Message } from "primereact/message";
import { Controller, useFormContext } from "react-hook-form";

interface IFeatureFlagItem {
  name: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

const paymentFlags: IFeatureFlagItem[] = [
  {
    name: "features.markPaidViaCash",
    label: "Legacy Cash Payments",
    description:
      "Allow marking orders paid via cash (Clear Health handles transfers)",
  },
  {
    name: "features.markPaidViaCashUnmanaged",
    label: "Unmanaged Cash Payments",
    description:
      "Allow marking orders paid via cash (account handles distribution internally)",
  },
  {
    name: "features.markPaidViaCheck",
    label: "Check Payments",
    description: "Allow marking orders paid via check",
  },
  {
    name: "features.orderPaymentNotesEnabled",
    label: "Order Payment Notes",
    description: "Enable payment notes on orders",
  },
  {
    name: "features.checkoutContinueButtonVisible",
    label: "Checkout Continue Button",
    description: "Show the continue button on checkout",
  },
  {
    name: "features.hideCheckoutWhenNotScheduled",
    label: "Hide Checkout When Not Scheduled",
    description:
      "Hide the continue button on checkout when the visit is not scheduled yet (also hidden when Checkout Continue Button is off)",
  },
  {
    name: "features.changeNextPaymentDueDateEnabled",
    label: "Patient Can Change Next Payment Due Date",
    description:
      "Show the 'Change the date' button on the patient's payment plan page (on by default; a patient may still only move the date once)",
  },
  {
    name: "features.recoverClearFeesOnRefunds",
    label: "Recover Clear Fees on Refunds",
    description:
      "Charge the provider to recover Clear's platform fee when refunds or money transfers reverse a payment (Stripe accounts only)",
  },
];

export function PaymentOptions() {
  const { control, setValue, watch } = useFormContext<any>();

  // Watch the mutually exclusive cash flags
  const markPaidViaCashUnmanaged = watch("features.markPaidViaCashUnmanaged");

  // Handle mutual exclusivity for cash payment flags
  const handleCashFlagChange = (
    fieldName: string,
    value: boolean,
    onChange: (value: boolean) => void
  ) => {
    onChange(value);

    // When enabling CashUnmanaged, disable legacy Cash
    if (fieldName === "features.markPaidViaCashUnmanaged" && value) {
      setValue("features.markPaidViaCash", false);
    }
    // When enabling legacy Cash, disable CashUnmanaged
    if (fieldName === "features.markPaidViaCash" && value) {
      setValue("features.markPaidViaCashUnmanaged", false);
    }
  };

  const isCashFlag = (name: string) =>
    name === "features.markPaidViaCash" ||
    name === "features.markPaidViaCashUnmanaged";

  return (
    <div className="flex flex-column gap-4 border-bottom-1 border-gray-200 pb-4">
      <span className="font-medium text-sm mb-2 text-gray-600">
        PAYMENT OPTIONS
      </span>
      {paymentFlags.map((flag) => (
        <div key={flag.name} className="flex gap-3">
          <Controller
            name={flag.name}
            control={control}
            render={({ field: { onChange, value } }) => (
              <InputSwitch
                inputId={flag.name}
                checked={value || false}
                disabled={flag.disabled}
                onChange={(e) => {
                  if (isCashFlag(flag.name)) {
                    handleCashFlagChange(flag.name, e.value, onChange);
                  } else {
                    onChange(e.value);
                  }
                }}
              />
            )}
          />
          <div className="flex flex-column">
            <span className="font-medium text-sm mb-1">{flag.label}</span>
            {flag.description && (
              <span className="text-sm text-gray-500">{flag.description}</span>
            )}
          </div>
        </div>
      ))}
      {/* Display warning when cash unmanaged is enabled */}
      {markPaidViaCashUnmanaged && (
        <Message
          severity="warn"
          text="Unmanaged Cash Payments are enabled. Orders marked as paid via this method will NOT be processed by Clear Health and will NOT appear on weekly payment reports."
          className="w-full"
        />
      )}
    </div>
  );
}
